import './LoginView.css'
import '../components/SocialAuthButtons.css'

import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import { getRejectionMessage } from '../lib/rejectionMessages'
import { session } from '../store'

import { Button } from '../components/Button'
import { FieldError } from '../components/FieldError'
import { Form } from '../components/Form'
import { SocialAuthButtons } from '../components/SocialAuthButtons'
import { TextField } from '../components/TextField'
import { useFormFieldErrors } from '../hooks/useFormFieldErrors'
import { isAnySocialAuthConfigured, type SocialAuthTokens } from '../lib/social-auth'

function postAuthPath(from: string | undefined): string {
  if (from && from !== '/' && from !== '/login' && !from.startsWith('/staff')) {
    return from
  }
  return '/profile'
}

export function LoginView() {
  const [formValue, setFormValue] = useState({ email: '', password: '' })
  const [linkPassword, setLinkPassword] = useState('')
  const [socialError, setSocialError] = useState<string | null>(null)

  const isLoading = useAtomValue(session.loginIsLoadingAtom)
  const rejectionError = useAtomValue(session.loginRejectionErrorAtom)
  const authStatus = useAtomValue(session.sessionStatusAtom)
  const passwordLink = useAtomValue(session.passwordLinkRequiredAtom)
  const accountNotFound = useAtomValue(session.oauthAccountNotFoundAtom)
  const sessionLogin = useSetAtom(session.loginAtom)
  const clearPasswordLink = useSetAtom(session.clearPasswordLinkAtom)
  const setLoginIssues = useSetAtom(session.loginIssuesAtom)
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from

  const serverErrors = useFormFieldErrors(session.loginIssuesAtom)
  const socialConfigured = isAnySocialAuthConfigured()

  // Declarative redirect: reliable with React Router even if imperative navigate races a re-render.
  if (authStatus === 'authenticated') {
    return <Navigate to={postAuthPath(from)} replace />
  }

  const navigateAfterAuth = () => {
    navigate(postAuthPath(from), { replace: true })
  }

  const handleFormChange = (value: { email: string; password: string }) => {
    setLoginIssues([])
    setFormValue(value)
  }

  const handleSubmit = async () => {
    setSocialError(null)
    setLoginIssues([])
    const result = await sessionLogin({
      type: 'emailAndPassword',
      email: formValue.email,
      password: formValue.password,
    })
    // Status flip also triggers <Navigate>; keep imperative navigate as a second path.
    if (result.success) navigateAfterAuth()
  }

  const handleSocialTokens = async (tokens: SocialAuthTokens) => {
    setSocialError(null)
    clearPasswordLink()
    setLinkPassword('')
    setLoginIssues([])

    const result = await sessionLogin(
      tokens.provider === 'google'
        ? {
            type: 'google',
            idToken: tokens.idToken,
            accessToken: tokens.accessToken,
          }
        : {
            type: 'facebook',
            accessToken: tokens.accessToken!,
          },
    )

    if (result.success) {
      navigateAfterAuth()
      return
    }

    if (result.accountNotFound) {
      setSocialError('No account found for that social login. Apply to join first.')
      return
    }

    if (result.passwordLinkRequired) return

    setSocialError('Social sign-in failed — please try again.')
  }

  const handleLinkPasswordSubmit = async () => {
    if (!passwordLink || !linkPassword) return
    setSocialError(null)
    setLoginIssues([])

    const result = await sessionLogin(
      passwordLink.provider === 'google'
        ? {
            type: 'google',
            idToken: passwordLink.idToken,
            accessToken: passwordLink.accessToken,
            password: linkPassword,
          }
        : {
            type: 'facebook',
            accessToken: passwordLink.accessToken!,
            password: linkPassword,
          },
    )

    if (result.success) navigateAfterAuth()
  }

  const handleCancelLink = () => {
    clearPasswordLink()
    setLinkPassword('')
  }

  return (
    <div className="login-view">
      <h2 className="login-view__title">Welcome back</h2>

      {rejectionError && (
        <div className="login-view__error-banner">
          {getRejectionMessage(rejectionError.code, rejectionError.daysRemaining)}
        </div>
      )}

      {accountNotFound && !passwordLink && (
        <div className="login-view__error-banner">
          No account found for that social login. <Link to="/register">Apply to join</Link>.
        </div>
      )}

      {socialError && <div className="login-view__error-banner">{socialError}</div>}

      {passwordLink?.flow === 'login' && (
        <div className="social-auth-link-panel">
          <p className="social-auth-link-panel__title">Confirm your password</p>
          <p className="social-auth-link-panel__message">
            An account already exists for <strong>{passwordLink.email}</strong>. Enter your password
            to link {passwordLink.provider === 'google' ? 'Google' : 'Facebook'} and sign in.
          </p>
          <Form
            value={{ password: linkPassword }}
            onChange={(value) => {
              setLoginIssues([])
              setLinkPassword(value.password)
            }}
            onSubmit={handleLinkPasswordSubmit}
            flags={serverErrors}
          >
            <TextField
              name="password"
              label="Password"
              kind="password"
              placeholder="Enter your password"
              required
              disabled={isLoading}
            />
            <FieldError
              name="password"
              text={{
                required: 'Password is required',
                invalidCredentials: 'Password is incorrect',
              }}
            />
            <div className="social-auth-link-panel__actions">
              <Button type="submit" kind="primary" disabled={isLoading || !linkPassword}>
                {isLoading ? 'Linking…' : 'Link and sign in'}
              </Button>
              <Button kind="ghost" disabled={isLoading} onClick={handleCancelLink}>
                Cancel
              </Button>
            </div>
          </Form>
        </div>
      )}

      {socialConfigured && !passwordLink && (
        <>
          <SocialAuthButtons
            mode="sign-in"
            disabled={isLoading}
            onTokens={handleSocialTokens}
            onError={setSocialError}
          />
          <div className="social-auth-divider">or</div>
        </>
      )}

      <Form
        className="login-view__form"
        value={formValue}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
        flags={serverErrors}
      >
        <TextField
          name="email"
          label="Email"
          placeholder="Enter your email"
          required
          email
          disabled={isLoading}
        />
        <FieldError
          name="email"
          text={{
            required: 'Email is required',
            email: 'Please enter a valid email',
            networkError: 'Unable to reach the server. Please try again.',
            loginFailed: 'Unable to sign in — please try again',
          }}
        />

        <TextField
          name="password"
          label="Password"
          kind="password"
          placeholder="Enter your password"
          required
          disabled={isLoading}
        />
        <FieldError
          name="password"
          text={{
            required: 'Password is required',
            invalidCredentials: 'Email or password unrecognized',
          }}
        />
        <FieldError
          name="provider"
          text={{
            invalidToken: 'Social sign-in failed — please try again',
            invalidCredentials: 'Unable to sign in with that account',
            loginFailed: 'Unable to sign in — please try again',
          }}
        />
        <Link to="/forgot-password" className="login-view__forgot-password">
          Forgot password?
        </Link>

        <div className="login-view__actions">
          <Button type="submit" kind="primary" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
          <Link to="/register" className="login-view__alt-action">
            Apply
          </Link>
        </div>
      </Form>
    </div>
  )
}
